import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("This reset link is missing its token.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset. You can now log in.");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "This reset link is invalid or has expired.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-missing-token">
          <h2 className="font-display text-2xl font-bold tracking-tight">This reset link is incomplete</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Open the latest email we sent and use the full button or link. Then you can choose a new password.
          </p>
          <Button asChild className="mt-6">
            <Link to="/forgot-password">Request a new reset email</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
          <h2 className="mt-6 font-display text-2xl font-bold tracking-tight">Password reset</h2>
          <p className="mt-2 text-sm text-muted-foreground">Redirecting you to sign in…</p>
          <Button className="mt-6" onClick={() => navigate("/login", { replace: true })} data-testid="reset-to-login">
            Go to login
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div data-testid="reset-password-page">
        <h2 className="font-display text-2xl font-bold tracking-tight">Set a new password</h2>
        <p className="mt-2 text-sm text-muted-foreground">Choose a strong password with at least 8 characters.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 pr-10"
                data-testid="reset-password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm password</Label>
            <Input
              id="confirm-password"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              className="h-11"
              data-testid="reset-confirm-input"
            />
          </div>
          <Button type="submit" className="w-full gap-2 font-semibold" disabled={submitting} data-testid="reset-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Remembered it?{" "}
          <Link to="/login" className="font-semibold text-primary underline-offset-4 hover:underline" data-testid="reset-back-login">
            Back to login
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
